import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { useObrasContext, Obra, ObraJornada, JornadaOrigen } from "@/contexts/ObrasContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { format } from "date-fns";
import { Loader2, Clock, AlertTriangle, Building2, Briefcase, ClipboardList, Truck, Lock } from "lucide-react";
import { toast } from "@/hooks/use-toast";

const jornadaSchema = z.object({
  origen: z.enum(['obra', 'orden_trabajo', 'cliente']),
  obra_id: z.string().optional(),
  orden_trabajo_id: z.string().optional(),
  cliente_id: z.string().optional(),
  fecha: z.string(),
  hora_inicio: z.string().min(1, "Hora de inicio es requerida"),
  hora_fin: z.string().optional(),
  descripcion_trabajo: z.string().optional(),
  tipo_trabajo: z.string().min(1, "Selecciona el tipo de trabajo"),
  es_hora_extra: z.boolean().default(false),
  uso_furgoneta: z.boolean().default(false),
}).refine(data => {
  if (data.origen === 'obra') return !!data.obra_id;
  if (data.origen === 'orden_trabajo') return !!data.orden_trabajo_id;
  if (data.origen === 'cliente') return !!data.cliente_id;
  return false;
}, {
  message: "Debes seleccionar una referencia según el tipo de origen",
  path: ["obra_id"]
});

type JornadaFormValues = z.infer<typeof jornadaSchema>;

interface OrdenTrabajoOption {
  id: string;
  numero_ot: string;
  titulo: string;
  cliente_nombre?: string;
}

interface ClienteOption {
  id: string;
  nombre: string;
}

interface OrdenTrabajoInfo {
  id: string;
  numero_ot: string;
  titulo: string;
  cliente_nombre?: string;
}

interface JornadaFormProps {
  obrasActivas: Obra[];
  jornada?: ObraJornada;
  defaultDate?: Date;
  defaultOrdenTrabajoId?: string;
  defaultClienteId?: string;
  defaultObraId?: string;
  obraInfo?: { id: string; nombre: string; cliente_nombre?: string };
  ordenTrabajoInfo?: OrdenTrabajoInfo;
  onSuccess: (jornadaId?: string) => void;
  onCancel: () => void;
}

export const JornadaForm: React.FC<JornadaFormProps> = ({
  obrasActivas,
  jornada,
  defaultDate,
  defaultOrdenTrabajoId,
  defaultClienteId,
  defaultObraId,
  obraInfo,
  ordenTrabajoInfo,
  onSuccess,
  onCancel,
}) => {
  const { perfil } = useAuth();
  const { createJornada, updateJornada } = useObrasContext();
  const [loading, setLoading] = useState(false);
  const [ordenesTrabajo, setOrdenesTrabajo] = useState<OrdenTrabajoOption[]>([]);
  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  const isEditing = !!jornada;
  const isOffice = perfil?.rol === 'admin' || perfil?.rol === 'gestor';
  // Ventana de cortesía de 15 min tras creación
  const dentroVentanaCortesia = jornada?.created_at
    ? (Date.now() - new Date(jornada.created_at).getTime()) < 15 * 60 * 1000
    : false;
  const isBlocked = isEditing && !isOffice && (
    jornada?.estado_validacion === 'aprobada' ||
    (jornada?.estado_validacion === 'pendiente' && !dentroVentanaCortesia)
  );
  const today = format(new Date(), 'yyyy-MM-dd');
  const defaultDateStr = defaultDate ? format(defaultDate, 'yyyy-MM-dd') : today;
  
  // Modo simplificado: cuando viene directamente de una OT o una Obra
  const modoSimplificado = !!ordenTrabajoInfo || !!obraInfo;

  // Determine default origin based on existing jornada or defaults
  const getDefaultOrigen = (): JornadaOrigen => {
    if (jornada) {
      if (jornada.obra_id) return 'obra';
      if (jornada.orden_trabajo_id) return 'orden_trabajo';
      if (jornada.cliente_id) return 'cliente';
    }
    if (obraInfo || defaultObraId) return 'obra';
    if (defaultOrdenTrabajoId) return 'orden_trabajo';
    if (defaultClienteId) return 'cliente';
    return 'obra';
  };

  const form = useForm<JornadaFormValues>({
    resolver: zodResolver(jornadaSchema),
    defaultValues: {
      origen: getDefaultOrigen(),
      obra_id: jornada?.obra_id || defaultObraId || obraInfo?.id || "",
      orden_trabajo_id: jornada?.orden_trabajo_id || defaultOrdenTrabajoId || "",
      cliente_id: jornada?.cliente_id || defaultClienteId || "",
      fecha: jornada?.fecha || defaultDateStr,
      hora_inicio: jornada?.hora_inicio || "",
      hora_fin: jornada?.hora_fin || "",
      descripcion_trabajo: jornada?.descripcion_trabajo || "",
      tipo_trabajo: jornada?.tipo_trabajo || "",
      es_hora_extra: jornada?.es_hora_extra || false,
      uso_furgoneta: (jornada as any)?.uso_furgoneta || false,
    },
  });

  const origen = form.watch('origen');

  // Load OTs and clients on mount
  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      try {
        // Load OTs assigned to this technician
        const { data: otsData, error: otsError } = await supabase
          .from('ordenes_trabajo')
          .select(`
            id, numero_ot, titulo,
            cliente:clientes(nombre)
          `)
          .eq('tecnico_asignado_id', perfil?.user_id)
          .in('estado', ['asignada', 'en_progreso']);

        if (otsError) throw otsError;

        const mappedOts: OrdenTrabajoOption[] = (otsData || []).map((ot: any) => ({
          id: ot.id,
          numero_ot: ot.numero_ot,
          titulo: ot.titulo,
          cliente_nombre: ot.cliente?.nombre || 'Sin cliente'
        }));
        setOrdenesTrabajo(mappedOts);

        // Load clients via RPC so técnicos see all clientes of their empresa
        const { data: clientesData, error: clientesError } = await supabase
          .rpc('get_clientes_para_formulario_trabajo');

        if (clientesError) throw clientesError;
        setClientes((clientesData || []).map((c: any) => ({ id: c.id, nombre: c.nombre })));
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoadingData(false);
      }
    };

    if (perfil?.user_id) {
      loadData();
    }
  }, [perfil?.user_id]);

  const startTimer = () => {
    const now = new Date();
    const currentTime = format(now, 'HH:mm');
    form.setValue('hora_inicio', currentTime);
    form.setValue('hora_fin', '');
  };

  const stopTimer = () => {
    const now = new Date();
    const currentTime = format(now, 'HH:mm');
    form.setValue('hora_fin', currentTime);
  };

  const onSubmit = async (data: JornadaFormValues) => {
    if (!perfil?.user_id) {
      toast({
        title: "Error",
        description: "No se pudo identificar el usuario",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const jornadaData = {
        obra_id: data.origen === 'obra' ? data.obra_id : null,
        orden_trabajo_id: data.origen === 'orden_trabajo' ? data.orden_trabajo_id : null,
        cliente_id: data.origen === 'cliente' ? data.cliente_id : null,
        fecha: data.fecha,
        hora_inicio: data.hora_inicio,
        hora_fin: data.hora_fin || null,
        descripcion_trabajo: data.descripcion_trabajo || null,
        tipo_trabajo: data.tipo_trabajo as "instalacion" | "puesta_en_marcha" | "auditoria" | "mantenimiento" | "visita_obra" | "correctivos",
        es_hora_extra: data.es_hora_extra,
        uso_furgoneta: data.uso_furgoneta,
      };

      if (isEditing && jornada) {
        const wasRejected = jornada.estado_validacion === 'rechazada';
        await updateJornada(jornada.id, {
          ...jornadaData,
          ...(wasRejected ? { estado_validacion: 'pendiente', motivo_rechazo: null } : {}),
        });
        onSuccess(jornada.id);
      } else {
        const created = await createJornada({
          ...jornadaData,
          tecnico_id: perfil.user_id,
        });
        onSuccess(created?.id);
      }
    } catch (error) {
      console.error('Error al guardar jornada:', error);
    } finally {
      setLoading(false);
    }
  };

  // Validate time logic
  const horaInicio = form.watch('hora_inicio');
  const horaFin = form.watch('hora_fin');
  
  const isTimeValid = () => {
    if (!horaInicio || !horaFin) return true;
    const [inicioHoras, inicioMinutos] = horaInicio.split(':').map(Number);
    const [finHoras, finMinutos] = horaFin.split(':').map(Number);
    const inicioMinutosTotales = inicioHoras * 60 + inicioMinutos;
    const finMinutosTotales = finHoras * 60 + finMinutos;
    return finMinutosTotales > inicioMinutosTotales;
  };

  const calculateHours = () => {
    if (!horaInicio || !horaFin || !isTimeValid()) return 0;
    const [inicioHoras, inicioMinutos] = horaInicio.split(':').map(Number);
    const [finHoras, finMinutos] = horaFin.split(':').map(Number);
    const inicioMinutosTotales = inicioHoras * 60 + inicioMinutos;
    const finMinutosTotales = finHoras * 60 + finMinutos;
    return (finMinutosTotales - inicioMinutosTotales) / 60;
  };

  // En modo simplificado, no necesitamos cargar datos
  if (loadingData && !modoSimplificado) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Cargando datos...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          {isEditing ? "Editar Jornada" : "Registrar Jornada"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className={`space-y-4 ${isBlocked ? 'opacity-70 pointer-events-none' : ''}`}>
            {isBlocked && (
              <div className="rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-sm flex items-start gap-2">
                <Lock className="h-4 w-4 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-700 dark:text-amber-400">Jornada bloqueada</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {jornada?.estado_validacion === 'aprobada'
                      ? 'Ha sido aprobada y solo un administrador puede modificarla.'
                      : 'Ha pasado la ventana de cortesía de 15 min. Solicita al administrador su rechazo para corregirla.'}
                  </p>
                </div>
              </div>
            )}
            {isEditing && jornada?.estado_validacion === 'rechazada' && (
              <div className="rounded-lg border-2 border-destructive bg-destructive/10 p-4 text-sm">
                <p className="font-semibold text-destructive flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> Jornada rechazada por el administrador
                </p>
                {jornada.motivo_rechazo && (
                  <p className="mt-2 text-foreground/90 font-medium">Motivo: {jornada.motivo_rechazo}</p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">Al guardar los cambios volverá a estado pendiente para nueva revisión.</p>
              </div>
            )}

            {/* Modo simplificado: mostrar info de la OT como lectura */}
            {modoSimplificado && ordenTrabajoInfo && (
              <div className="bg-muted/50 border rounded-lg p-4 space-y-1">
                <div className="flex items-center gap-2">
                  <ClipboardList className="h-4 w-4 text-green-500" />
                  <span className="font-mono text-sm font-medium">{ordenTrabajoInfo.numero_ot}</span>
                </div>
                <p className="font-medium">{ordenTrabajoInfo.titulo}</p>
                {ordenTrabajoInfo.cliente_nombre && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {ordenTrabajoInfo.cliente_nombre}
                  </p>
                )}
              </div>
            )}

            {/* Modo simplificado: mostrar info de la Obra como lectura */}
            {modoSimplificado && obraInfo && (
              <div className="bg-muted/50 border rounded-lg p-4 space-y-1">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-blue-500" />
                  <span className="font-medium">{obraInfo.nombre}</span>
                </div>
                {obraInfo.cliente_nombre && (
                  <p className="text-sm text-muted-foreground">{obraInfo.cliente_nombre}</p>
                )}
              </div>
            )}

            {/* Origin selector - Solo en modo normal */}
            {!modoSimplificado && (
              <FormField
                control={form.control}
                name="origen"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>¿A qué tipo de trabajo corresponde?</FormLabel>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="grid grid-cols-3 gap-4"
                      >
                        <div className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                          <RadioGroupItem value="obra" id="origen-obra" />
                          <label htmlFor="origen-obra" className="flex items-center gap-2 cursor-pointer flex-1">
                            <Building2 className="h-4 w-4 text-blue-500" />
                            <span className="text-sm font-medium">Obra</span>
                          </label>
                        </div>
                        <div className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                          <RadioGroupItem value="orden_trabajo" id="origen-ot" />
                          <label htmlFor="origen-ot" className="flex items-center gap-2 cursor-pointer flex-1">
                            <ClipboardList className="h-4 w-4 text-green-500" />
                            <span className="text-sm font-medium">OT</span>
                          </label>
                        </div>
                        <div className="flex items-center space-x-2 border rounded-lg p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                          <RadioGroupItem value="cliente" id="origen-cliente" />
                          <label htmlFor="origen-cliente" className="flex items-center gap-2 cursor-pointer flex-1">
                            <Briefcase className="h-4 w-4 text-purple-500" />
                            <span className="text-sm font-medium">Cliente</span>
                          </label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Obra selector - Solo en modo normal */}
            {!modoSimplificado && origen === 'obra' && (
              <FormField
                control={form.control}
                name="obra_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Obra</FormLabel>
                    <FormControl>
                      <SearchableSelect
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Selecciona una obra"
                        searchPlaceholder="Buscar obra o cliente..."
                        emptyMessage="No se encontraron obras"
                        options={obrasActivas.map((obra) => ({
                          value: obra.id,
                          label: `${obra.nombre}${obra.cliente?.nombre ? ` - ${obra.cliente.nombre}` : ''}`,
                          searchText: `${obra.nombre} ${obra.cliente?.nombre || ''}`,
                        }))}
                      />
                    </FormControl>
                    {obrasActivas.length === 0 && (
                      <p className="text-sm text-muted-foreground">No hay obras activas disponibles</p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* OT selector - Solo en modo normal */}
            {!modoSimplificado && origen === 'orden_trabajo' && (
              <FormField
                control={form.control}
                name="orden_trabajo_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Orden de Trabajo</FormLabel>
                    <FormControl>
                      <SearchableSelect
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Selecciona una OT"
                        searchPlaceholder="Buscar por nº, título o cliente..."
                        emptyMessage="No se encontraron OTs"
                        options={ordenesTrabajo.map((ot) => ({
                          value: ot.id,
                          label: `${ot.numero_ot} - ${ot.titulo} (${ot.cliente_nombre})`,
                          searchText: `${ot.numero_ot} ${ot.titulo} ${ot.cliente_nombre || ''}`,
                        }))}
                      />
                    </FormControl>
                    {ordenesTrabajo.length === 0 && (
                      <p className="text-sm text-muted-foreground">No tienes OTs asignadas activas</p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Cliente selector - Solo en modo normal */}
            {!modoSimplificado && origen === 'cliente' && (
              <FormField
                control={form.control}
                name="cliente_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cliente</FormLabel>
                    <FormControl>
                      <SearchableSelect
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Selecciona un cliente"
                        searchPlaceholder="Buscar cliente..."
                        emptyMessage="No se encontraron clientes"
                        options={clientes.map((cliente) => ({
                          value: cliente.id,
                          label: cliente.nombre,
                          searchText: cliente.nombre,
                        }))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="fecha"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="hora_inicio"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hora de Inicio</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      {!isEditing && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={startTimer}
                        >
                          Ahora
                        </Button>
                      )}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hora_fin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hora de Fin (Opcional)</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      {!isEditing && horaInicio && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={stopTimer}
                        >
                          Ahora
                        </Button>
                      )}
                    </div>
                    <FormMessage />
                    {!isTimeValid() && horaFin && (
                      <p className="text-sm text-destructive">
                        La hora de fin debe ser posterior a la de inicio
                      </p>
                    )}
                  </FormItem>
                )}
              />
            </div>

            {horaInicio && horaFin && isTimeValid() && (
              <div className="bg-muted p-3 rounded-md">
                <p className="text-sm font-medium">
                  Duración calculada: {calculateHours().toFixed(2)} horas
                </p>
              </div>
            )}

            <FormField
              control={form.control}
              name="tipo_trabajo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Trabajo</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona el tipo de trabajo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="instalacion">Instalación</SelectItem>
                      <SelectItem value="puesta_en_marcha">Puesta en marcha</SelectItem>
                      <SelectItem value="auditoria">Auditoría</SelectItem>
                      <SelectItem value="mantenimiento">Mantenimiento</SelectItem>
                      <SelectItem value="visita_obra">Visita obra</SelectItem>
                      <SelectItem value="correctivos">Correctivos</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="descripcion_trabajo"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción del Trabajo (Opcional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe brevemente el trabajo realizado..."
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="es_hora_extra"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de horas</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value ? "extra" : "normal"}
                      onValueChange={(v) => field.onChange(v === "extra")}
                      className="grid grid-cols-2 gap-3"
                    >
                      <label
                        htmlFor="hr-normal"
                        className={`flex items-center gap-3 border-2 rounded-lg p-3 cursor-pointer transition-all ${
                          !field.value
                            ? "border-primary bg-primary/5"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        <RadioGroupItem value="normal" id="hr-normal" />
                        <Clock className="h-4 w-4 text-primary" />
                        <div className="leading-tight">
                          <div className="text-sm font-medium">Horas normales</div>
                          <div className="text-xs text-muted-foreground">Tarifa estándar</div>
                        </div>
                      </label>
                      <label
                        htmlFor="hr-extra"
                        className={`flex items-center gap-3 border-2 rounded-lg p-3 cursor-pointer transition-all ${
                          field.value
                            ? "border-orange-500 bg-orange-500/10"
                            : "border-border hover:bg-muted/50"
                        }`}
                      >
                        <RadioGroupItem value="extra" id="hr-extra" />
                        <AlertTriangle className="h-4 w-4 text-orange-500" />
                        <div className="leading-tight">
                          <div className="text-sm font-medium">Horas extra</div>
                          <div className="text-xs text-muted-foreground">Tarifa extra</div>
                        </div>
                      </label>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="uso_furgoneta"
              render={({ field }) => (
                <FormItem>
                  <label
                    className={`flex items-center gap-3 border-2 rounded-lg p-3 cursor-pointer transition-all ${
                      field.value ? "border-primary bg-primary/5" : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <Truck className="h-4 w-4 text-primary" />
                    <div className="leading-tight">
                      <div className="text-sm font-medium">Uso de furgoneta</div>
                      <div className="text-xs text-muted-foreground">
                        Aplica el importe de furgoneta de la tarifa del técnico
                      </div>
                    </div>
                  </label>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading || !isTimeValid() || isBlocked}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isBlocked && <Lock className="h-4 w-4 mr-2" />}
                {isEditing ? "Actualizar" : "Guardar"} Jornada
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};